import { useState } from "react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { Field, FileField, SuccessPanel, useEnquiryForm, inputCls } from "../components/forms/fields";
import { FadeUp } from "../components/site/Reveal";

const INITIAL = {
    fullName: "",
    jobTitle: "",
    companyName: "",
    companyWebsite: "",
    country: "",
    businessEmail: "",
    phone: "",
    buyerType: "",
    productRequired: "",
    detailedSpecification: "",
    requiredQuantity: "",
    orderFrequency: "",
    packagingRequirements: "",
    privateLabel: "",
    certificationsRequired: "",
    targetPriceRange: "",
    destinationCountry: "",
    destinationPort: "",
    incoterm: "",
    requiredDeliveryDate: "",
    additionalInfo: "",
};

export default function SourcingEnquiry() {
    const [form, setForm] = useState(INITIAL);
    const [file, setFile] = useState(null);
    const { submitting, reference, submit } = useEnquiryForm("sourcing");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        submit(form, file, true);
    };

    return (
        <div className="grain bg-navy-deep" data-testid="sourcing-enquiry-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "Vietnam Sourcing Enquiry | Send Your Product Requirement — Viet Medica Ltd",
                        description:
                            "Send your product requirement to our Vietnam sourcing team — specifications, quantities, packaging, certifications and destination details reviewed by local agents.",
                        path: "/vietnam-sourcing-enquiry",
                    }}
                    crumbs={[{ label: "Vietnam Sourcing", href: "/vietnam-sourcing-agent" }, { label: "Product Requirement" }]}
                    eyebrow="Trading and Sourcing from Vietnam"
                    title="Send Your Product"
                    titleEm="Requirement."
                    copy="The more detail you provide, the more accurately our Vietnam agents can review your requirement. Your enquiry goes to a dedicated Vietnam sourcing category, reviewed by our local team."
                />

                <section className="bg-navy-deep py-16 lg:py-24">
                    <div className="mx-auto max-w-3xl px-5 lg:px-10">
                        {reference ? (
                            <SuccessPanel reference={reference} categoryLabel="Vietnam sourcing" />
                        ) : (
                            <FadeUp>
                                <form onSubmit={onSubmit} className="space-y-6 border border-white/10 bg-navy-surface p-7 lg:p-10" data-testid="sourcing-enquiry-form">
                                    <h2 className="font-serif text-xl font-normal text-ivory">About You</h2>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <Field label="Full Name" htmlFor="sc-name" required>
                                            <input id="sc-name" data-testid="sc-name" required className={inputCls} value={form.fullName} onChange={set("fullName")} />
                                        </Field>
                                        <Field label="Job Title" htmlFor="sc-title">
                                            <input id="sc-title" data-testid="sc-title" className={inputCls} value={form.jobTitle} onChange={set("jobTitle")} />
                                        </Field>
                                        <Field label="Company Name" htmlFor="sc-company" required>
                                            <input id="sc-company" data-testid="sc-company" required className={inputCls} value={form.companyName} onChange={set("companyName")} />
                                        </Field>
                                        <Field label="Company Website" htmlFor="sc-website">
                                            <input id="sc-website" data-testid="sc-website" type="url" className={inputCls} value={form.companyWebsite} onChange={set("companyWebsite")} placeholder="https://" />
                                        </Field>
                                        <Field label="Country" htmlFor="sc-country" required>
                                            <input id="sc-country" data-testid="sc-country" required className={inputCls} value={form.country} onChange={set("country")} />
                                        </Field>
                                        <Field label="Business Email" htmlFor="sc-email" required>
                                            <input id="sc-email" data-testid="sc-email" required type="email" className={inputCls} value={form.businessEmail} onChange={set("businessEmail")} />
                                        </Field>
                                        <Field label="Telephone or WhatsApp" htmlFor="sc-phone" required>
                                            <input id="sc-phone" data-testid="sc-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} />
                                        </Field>
                                        <Field label="You are a" htmlFor="sc-buyertype" required>
                                            <select id="sc-buyertype" data-testid="sc-buyertype" required className={inputCls} value={form.buyerType} onChange={set("buyerType")}>
                                                <option value="">Select…</option>
                                                <option>Buyer</option>
                                                <option>Importer</option>
                                                <option>Distributor</option>
                                                <option>Retailer</option>
                                            </select>
                                        </Field>
                                    </div>

                                    <h2 className="pt-4 font-serif text-xl font-normal text-ivory">Your Requirement</h2>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <Field label="Product Required" htmlFor="sc-product" required>
                                            <input id="sc-product" data-testid="sc-product" required className={inputCls} value={form.productRequired} onChange={set("productRequired")} />
                                        </Field>
                                        <Field label="Required Quantity" htmlFor="sc-quantity" required>
                                            <input id="sc-quantity" data-testid="sc-quantity" required className={inputCls} value={form.requiredQuantity} onChange={set("requiredQuantity")} />
                                        </Field>
                                        <Field label="Order Frequency" htmlFor="sc-frequency">
                                            <input id="sc-frequency" data-testid="sc-frequency" className={inputCls} value={form.orderFrequency} onChange={set("orderFrequency")} placeholder="e.g. Monthly, one-off" />
                                        </Field>
                                        <Field label="Packaging Requirements" htmlFor="sc-packaging">
                                            <input id="sc-packaging" data-testid="sc-packaging" className={inputCls} value={form.packagingRequirements} onChange={set("packagingRequirements")} />
                                        </Field>
                                        <Field label="Private-Label Requirements" htmlFor="sc-privatelabel">
                                            <select id="sc-privatelabel" data-testid="sc-privatelabel" className={inputCls} value={form.privateLabel} onChange={set("privateLabel")}>
                                                <option value="">Select…</option>
                                                <option>Yes</option>
                                                <option>No</option>
                                                <option>To be discussed</option>
                                            </select>
                                        </Field>
                                        <Field label="Certifications Required" htmlFor="sc-certs">
                                            <input id="sc-certs" data-testid="sc-certs" className={inputCls} value={form.certificationsRequired} onChange={set("certificationsRequired")} />
                                        </Field>
                                        <Field label="Target Price Range" htmlFor="sc-price">
                                            <input id="sc-price" data-testid="sc-price" className={inputCls} value={form.targetPriceRange} onChange={set("targetPriceRange")} />
                                        </Field>
                                        <Field label="Destination Country" htmlFor="sc-destcountry" required>
                                            <input id="sc-destcountry" data-testid="sc-destcountry" required className={inputCls} value={form.destinationCountry} onChange={set("destinationCountry")} />
                                        </Field>
                                        <Field label="Destination Port" htmlFor="sc-destport">
                                            <input id="sc-destport" data-testid="sc-destport" className={inputCls} value={form.destinationPort} onChange={set("destinationPort")} />
                                        </Field>
                                        <Field label="Preferred Incoterm" htmlFor="sc-incoterm">
                                            <select id="sc-incoterm" data-testid="sc-incoterm" className={inputCls} value={form.incoterm} onChange={set("incoterm")}>
                                                <option value="">Select…</option>
                                                {["EXW", "FOB", "CFR", "CIF", "DAP", "DDP", "Other"].map((i) => (
                                                    <option key={i}>{i}</option>
                                                ))}
                                            </select>
                                        </Field>
                                        <Field label="Required Delivery Date" htmlFor="sc-delivery">
                                            <input id="sc-delivery" data-testid="sc-delivery" className={inputCls} value={form.requiredDeliveryDate} onChange={set("requiredDeliveryDate")} />
                                        </Field>
                                    </div>
                                    <Field label="Detailed Specification" htmlFor="sc-spec" required>
                                        <textarea id="sc-spec" data-testid="sc-spec" required rows={4} className={inputCls} value={form.detailedSpecification} onChange={set("detailedSpecification")} placeholder="Grade, size, processing, standards…" />
                                    </Field>
                                    <Field label="Additional Information" htmlFor="sc-additional">
                                        <textarea id="sc-additional" data-testid="sc-additional" rows={3} className={inputCls} value={form.additionalInfo} onChange={set("additionalInfo")} />
                                    </Field>
                                    <Field label="Supporting Document" htmlFor="sc-file" hint="Optional. PDF, PNG or JPG, max 3 MB.">
                                        <FileField id="sc-file" file={file} onChange={setFile} />
                                    </Field>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="sc-submit"
                                        className="w-full bg-gold px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-navy-deep transition-colors hover:bg-gold-bright disabled:opacity-50"
                                    >
                                        {submitting ? "Sending…" : "Send Product Requirement"}
                                    </button>
                                </form>
                            </FadeUp>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
